import type { Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { computed, ref, watch } from 'vue'

export interface BreadcrumbItem {
  route?: RouteLocationRaw
  label: string
  icon?: string
  isTranslated?: boolean // Whether the label should be translated
  isCurrent?: boolean
}

// Global reactive state for breadcrumbs
const customBreadcrumbs: Ref<BreadcrumbItem[]> = ref([])
const isCustomBreadcrumbMode = ref(false)

// Global reactive state for custom page title
const customPageTitle: Ref<string | null> = ref(null)

// Global reactive state for semester selector
const showSemesterSelector = ref(false)

export function useBreadcrumbs() {
  const router = useRouter()
  const { setMenuRouteContext } = useLayout()

  /**
   * Clear custom breadcrumbs and return to automatic generation
   */
  const clearBreadcrumbs = () => {
    customBreadcrumbs.value = []
    isCustomBreadcrumbMode.value = false
  }

  /**
   * Set a custom page title that overrides breadcrumb-based title generation
   */
  const setPageTitle = (title: string | null) => {
    customPageTitle.value = title
  }

  /**
   * Clear the custom page title and return to breadcrumb-based title generation
   */
  const clearPageTitle = () => {
    customPageTitle.value = null
  }

  /**
   * Enable semester selector display in breadcrumb bar
   */
  const enableSemesterSelector = () => {
    showSemesterSelector.value = true
  }

  /**
   * Disable semester selector display in breadcrumb bar
   */
  const disableSemesterSelector = () => {
    showSemesterSelector.value = false
  }
  // Clear custom breadcrumbs, page title, and semester selector when route changes to allow fresh start for each page
  // Watch both name and params to handle navigation between different instances (e.g., /events/1 → /events/2)
  // but ignore path changes within catch-all params (e.g., /offer → /offer/SPZ → /offer/SPZ/EN/A2)
  watch(
    () => {
      const name = router.currentRoute.value.name
      // Only include non-catch-all params (exclude headingPath used for filtering)
      // Serialize to string for stable comparison
      const relevantParams = Object.entries(router.currentRoute.value.params)
        .filter(([key]) => key !== 'headingPath')
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => {
          const normalizedValue = Array.isArray(v) ? v.join('/') : typeof v === 'string' ? v : ''
          return `${k}=${normalizedValue}`
        })
        .join('&')
      return `${String(name)}?${relevantParams}`
    },
    () => {
      isCustomBreadcrumbMode.value = false
      customBreadcrumbs.value = []
      customPageTitle.value = null
      showSemesterSelector.value = false
    },
  )

  /**
   * Set custom breadcrumbs for the current page
   * This will override the automatic breadcrumb generation
   */
  const setBreadcrumbs = (breadcrumbs: BreadcrumbItem[]) => {
    customBreadcrumbs.value = breadcrumbs.map((item, index) => ({
      ...item,
      isCurrent: index === breadcrumbs.length - 1,
    }))
    isCustomBreadcrumbMode.value = true
  }

  /**
   * Add a single breadcrumb item to the current breadcrumbs
   * Useful for appending dynamic content to existing breadcrumbs
   */
  const addBreadcrumb = (breadcrumb: BreadcrumbItem) => {
    // Mark the previous last item as not current
    const lastIndex = customBreadcrumbs.value.length - 1
    if (lastIndex >= 0) {
      const previous = customBreadcrumbs.value[lastIndex]
      if (previous) {
        previous.isCurrent = false
      }
    }

    customBreadcrumbs.value.push({
      ...breadcrumb,
      isCurrent: true,
    })
    isCustomBreadcrumbMode.value = true
  }

  /**
   * Generate automatic breadcrumbs from the current route
   * This is the fallback when no custom breadcrumbs are set
   */
  const generateAutomaticBreadcrumbs = (): BreadcrumbItem[] => {
    const current = router.currentRoute.value
    const pathSegments = current.path.split('/').filter(Boolean)

    if (pathSegments.length === 0) {
      return []
    }

    // Generate incremental paths: ['/users', '/users/1', '/users/1/reports']
    const pathIncrementalSegments = pathSegments.map(
      (_, idx) => `/${pathSegments.slice(0, idx + 1).join('/')}`,
    )

    const matchedRoutes = pathIncrementalSegments.map((s: string) => router.resolve(s))
    const items = matchedRoutes.map((r, index: number) => {
      const routeName = typeof r.name === 'string' ? r.name : undefined
      const matchedRecord = r.matched[r.matched.length - 1]
      const matchedRouteName =
        matchedRecord && typeof matchedRecord.name === 'string' ? matchedRecord.name : undefined
      const matchedRoutePath =
        matchedRecord && typeof matchedRecord.path === 'string' ? matchedRecord.path : undefined
      const labelSource =
        routeName || matchedRouteName || matchedRoutePath || pathIncrementalSegments[index] || ''
      const label = `router.pages.${labelSource
        .split('/')
        .filter((x: string) => !!x)
        .map((x: string) => x.replace(/^:/, '').replace(/[()]/g, ''))
        .join('-')
        .replace(/\[/g, '')
        .replace(/\]/g, '')
        .replace('...', '')}`
      const icon = typeof r.meta?.icon === 'string' ? r.meta.icon : undefined

      return {
        route: r.path,
        label,
        icon,
        isCurrent: index === matchedRoutes.length - 1,
      }
    })

    // Consecutive segments can resolve to the same route record — `/user` is an alias of
    // `/user/settings`, and `/user/settings` redirects to its first tab — so both segments yield
    // the same label. Keep the deepest segment of each such run.
    return items.filter((item, index) => items[index + 1]?.label !== item.label)
  }

  /**
   * Get the current breadcrumbs (either custom or automatic)
   */
  const breadcrumbs = computed(() => {
    if (isCustomBreadcrumbMode.value && customBreadcrumbs.value.length > 0) {
      return customBreadcrumbs.value
    }
    return generateAutomaticBreadcrumbs()
  })
  /**
   * Get the current page title from custom title, last breadcrumb item, or fallback
   * Priority: custom title > last breadcrumb > fallback
   */
  const pageTitle = computed(() => {
    // If a custom page title is set, use it first
    if (customPageTitle.value) {
      return customPageTitle.value
    }

    // Otherwise, get title from breadcrumbs
    const currentBreadcrumbs = breadcrumbs.value
    if (currentBreadcrumbs.length === 0) {
      return 'common.terms.noTitle'
    }

    // Get the last breadcrumb (current page)
    const lastBreadcrumb = currentBreadcrumbs[currentBreadcrumbs.length - 1]
    return lastBreadcrumb?.label ?? 'common.terms.noTitle'
  })

  /**
   * Helper function to build workspace breadcrumbs
   */
  const buildWorkspaceBreadcrumbs = (
    wsId: string | number,
    wsName: string,
    currentPage?: string,
  ): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [
      {
        route: '/workspaces',
        label: 'router.pages.workspaces',
      },
      {
        route: { name: '/workspaces/[id]/overview', params: { id: wsId } },
        label: wsName,
        isTranslated: true,
        icon: 'i-lucide-building-2',
      },
    ]

    if (currentPage) {
      breadcrumbs.push({
        label: currentPage,
        isCurrent: true,
      })
    }

    return breadcrumbs
  }

  /**
   * Helper function to build event breadcrumbs
   *
   * Also publishes the menu route context so the sidebar marks the same section
   * the breadcrumb trail starts with: the workspace for workspace events, the
   * global event management for everything else.
   */
  const buildEventBreadcrumbs = (
    eventId: string | number,
    eventName: string,
    currentPage?: string,
    workspaceId?: string | number,
    workspaceName?: string,
  ): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = []

    setMenuRouteContext(
      workspaceId
        ? {
            routePrefix: `/manage/events/${eventId}`,
            menuPath: `/workspaces/${workspaceId}`,
          }
        : null,
    )

    if (workspaceId && workspaceName) {
      breadcrumbs.push({
        route: { name: '/workspaces/[id]/overview', params: { id: workspaceId } },
        label: workspaceName,
        isTranslated: true,
        icon: 'i-lucide-building-2',
      })
      breadcrumbs.push({
        route: { name: '/workspaces/[id]/events', params: { id: workspaceId } },
        label: 'router.pages.workspaces-id-events',
        isTranslated: false,
      })
    } else {
      breadcrumbs.push({
        route: '/manage/events',
        label: 'router.pages.manage',
      })
    }

    breadcrumbs.push({
      route: { name: '/manage/events/[id]/overview', params: { id: eventId } },
      label: eventName,
      isTranslated: true,
      icon: 'i-lucide-book',
    })

    if (currentPage) {
      breadcrumbs.push({
        label: currentPage,
        isCurrent: true,
      })
    }
    return breadcrumbs
  }

  /**
   * Helper function to build group breadcrumbs
   */
  const buildGroupBreadcrumbs = (
    wsId: string | number,
    wsName: string,
    groupId: string | number,
    groupName: string,
    currentPage?: string,
  ): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [
      {
        route: '/workspaces',
        label: 'router.pages.workspaces',
        icon: 'i-lucide-building-2',
      },
      {
        route: { name: '/workspaces/[id]/overview', params: { id: wsId } },
        label: wsName,
        icon: 'i-lucide-building-2',
      },
      {
        route: { name: '/workspaces/[id]/groups', params: { id: wsId } },
        label: 'router.pages.workspaces-details-groups',
        icon: 'i-lucide-users',
      },
      {
        route: { name: '/manage/groups', query: { groupId: String(groupId) } },
        label: groupName,
        icon: 'i-lucide-users',
      },
    ]

    if (currentPage) {
      breadcrumbs.push({
        label: currentPage,
        isCurrent: true,
      })
    }

    return breadcrumbs
  }
  return {
    breadcrumbs,
    setBreadcrumbs,
    addBreadcrumb,
    clearBreadcrumbs,
    setPageTitle,
    clearPageTitle,
    enableSemesterSelector,
    disableSemesterSelector,
    buildWorkspaceBreadcrumbs,
    buildEventBreadcrumbs,
    buildGroupBreadcrumbs,
    isCustomBreadcrumbMode,
    pageTitle,
    showSemesterSelector: computed(() => showSemesterSelector.value),
  }
}
